<script setup lang="ts">
import { onBeforeMount, provide, ref } from 'vue';

import {
  TSButton,
  TSButtonFilter,
  TSButtonSearch,
  getTransactionRole,
  useLoadingStore,
  useToast,
} from 'tsv2-library';

import TransactionDetail from '@/components/common/TransactionDetail.vue';

import BorrowingServices from '@/services/borrowing.service';

import useTableStore from '@/store/table.store';
import { ApprovalTransaction } from '@/types/approval.type';
import {
  BorrowingTransactionDetail,
  BorrowingTransactionRequest,
} from '@/types/borrowing.type';
import {
  BorrowingRequestFetchParams,
  BorrowingRequestFilterParams,
} from '@/types/params.type';
import { SettingTransaction } from '@/types/setting-transaction.type';
import {
  getApproverList,
  getSettingTransactionData,
} from '@/utils/getData.utils';
import { FetchResponse } from 'tsv2-library/dist/src/components/v2/DataTable/DataTable.vue';
import { Router } from 'vue-router';
import BorrowDetailBulkAction from './BorrowDetailBulkAction.vue';
import BorrowDetailFilter from './BorrowDetailFilter.vue';
import BorrowDetailTable from './BorrowDetailTable.vue';

onBeforeMount(async () => {
  await getSettingTransactionData(setLoading, settingTransaction, toast);

  // Get the transaction id from session storage.
  if (props.type === 'History') {
    transactionId.value =
      sessionStorage.getItem('assetBorrowHistoryTransactionId') ?? undefined;
  } else {
    transactionId.value =
      sessionStorage.getItem('assetBorrowTransactionId') ?? undefined;
  }

  if (transactionId.value) {
    // Fetch the essential data.
    await getTransactionDetail();
    await getApproverList(transactionId, setLoading, approvalList, toast);

    // Force the table to re-fetch.
    incrementTableKey();
  } else {
    props.router?.back();
  }
});

const toast = useToast();
const { setLoading } = useLoadingStore();
const { incrementTableKey } = useTableStore();

const props = defineProps<{
  type?: 'History';
  isBorrowed?: boolean;
  router?: Router;
  borrower?: boolean;
}>();

const filterParams = ref<BorrowingRequestFilterParams>({});

const transactionId = ref<string>();
const transactionDetail = ref<BorrowingTransactionDetail>();
const approvalList = ref<ApprovalTransaction[]>();
const settingTransaction = ref<SettingTransaction>();
const selectedData = ref<BorrowingTransactionRequest[]>([]);
const fetchedData = ref<BorrowingTransactionRequest[]>([]);

const searchModel = ref<string>();
const showFilter = ref<boolean>(false);

const { manager } = getTransactionRole('borrowingRole');

/**
 * Get the transaction detail from the server.
 */
const getTransactionDetail = async (): Promise<void> => {
  if (!transactionId.value) return;
  try {
    setLoading(true);

    const { data } = await BorrowingServices.getBorrowingTransactionDetail(
      transactionId.value,
    );

    transactionDetail.value = data.data;
  } catch (error) {
    toast.add({
      error,
      message: 'Error, failed to fetch transaction detail',
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
};

const getTransactionRequestData = async (
  queryParams: Omit<BorrowingRequestFetchParams, 'prelist'>,
): Promise<FetchResponse | undefined> => {
  if (!transactionId.value || !transactionDetail.value) return;

  queryParams.children = JSON.stringify([
    transactionDetail.value?.borrower._id,
  ]);

  try {
    const { data } = await BorrowingServices.getBorrowingTransactionRequest(
      transactionId.value,
      queryParams,
    );

    fetchedData.value = data.data.data;

    return data.data;
  } catch (error) {
    toast.add({
      error,
      message: 'Error, failed to fetch transaction request',
      severity: 'error',
    });
  }
};

const handleClose = (): void => {
  sessionStorage.removeItem('assetBorrowTransactionId');
  sessionStorage.removeItem('assetBorrowHistoryTransactionId');

  if (window.Cypress) return;
  props.router?.back();
};

provide('approvalList', approvalList);
provide('settingTransaction', settingTransaction);
provide('transactionDetail', transactionDetail);
provide('getTransactionDetail', getTransactionDetail);
provide('transactionId', transactionId);
</script>
<template>
  <TransactionDetail type="Detail" />
  <div
    class="flex justify-between items-center"
    data-ts-section="borrow-detail-header"
  >
    <BorrowDetailBulkAction
      v-if="(props.type !== 'History' && manager) || !props.borrower"
      v-model:selected-data="selectedData"
    />
    <span />
    <div
      class="flex gap-1 items-center"
      data-ts-section="borrow-detail-header-action"
    >
      <TSButtonSearch
        @search="searchModel = $event"
        data-ts-section="borrow-detail-search-button"
      />
      <TSButtonFilter
        v-model:show-filter="showFilter"
        data-ts-section="borrow-detail-filter-button"
      />
    </div>
  </div>
  <BorrowDetailFilter
    v-show="showFilter"
    :is-borrowed
    :transaction-id="transactionId ?? ''"
    :type
    @apply-filter="filterParams = $event"
  />
  <BorrowDetailTable
    v-model:selected-data="selectedData"
    :borrower
    :data="fetchedData"
    :fetch-function="getTransactionRequestData"
    :filters="filterParams"
    :is-borrowed
    :search="searchModel"
    :type
  />
  <div class="flex justify-end" data-ts-section="borrow-detail-footer">
    <TSButton
      @click="handleClose"
      data-ts-section="close-button"
      label="Close"
      outlined
    />
  </div>
</template>
